import { WORLD_MAP_CLUSTER_BBOX_KEY_PRECISION, WORLD_MAP_CLUSTER_BBOX_PADDING_RATIO, WORLD_MAP_CLUSTER_MAX_PADDING_TILT_DEG, WORLD_MAP_CLUSTER_WORLD_BBOX_MAX_ZOOM, WORLD_MAP_LONGITUDE_SPAN, WORLD_MAP_MAX_LATITUDE, WORLD_MAP_MAX_LONGITUDE, WORLD_MAP_MIN_LONGITUDE } from "@components/world-map/clustering/world-map-cluster-constants";

/** Geographic extent of the viewport, already projected out of Web Mercator. */

/** Camera position translated into the bbox and integer zoom supercluster queries with. */

/** Full-globe bbox, used whenever the viewport extent cannot be trusted. */
const WORLD_BBOX = [WORLD_MAP_MIN_LONGITUDE, -WORLD_MAP_MAX_LATITUDE, WORLD_MAP_MAX_LONGITUDE, WORLD_MAP_MAX_LATITUDE];

/** Degrees in half a turn, for converting the camera tilt to radians. */
const DEGREES_PER_HALF_TURN = 180;

/** Wraps a longitude back into [-180, 180]. Supercluster splits wrapped bboxes itself. */
function normalizeLongitude(lng) {
  const shifted = lng + WORLD_MAP_MAX_LONGITUDE;
  const wrapped = (shifted % WORLD_MAP_LONGITUDE_SPAN + WORLD_MAP_LONGITUDE_SPAN) % WORLD_MAP_LONGITUDE_SPAN;
  return wrapped - WORLD_MAP_MAX_LONGITUDE;
}
function clampLatitude(lat) {
  return Math.min(WORLD_MAP_MAX_LATITUDE, Math.max(-WORLD_MAP_MAX_LATITUDE, lat));
}

/**
 * How far past the viewport extent to query.
 *
 * `view.extent` covers only the nadir footprint and is identical whether the
 * camera is top-down or tilted, so on a tilted view everything between the
 * footprint and the horizon would be missing. Padding therefore grows with the
 * tilt, roughly with how much further the ground stretches away from the camera.
 */
function getPaddingRatio(tiltDegrees) {
  const clampedTilt = Math.min(Math.abs(tiltDegrees), WORLD_MAP_CLUSTER_MAX_PADDING_TILT_DEG);
  const tiltRadians = clampedTilt * Math.PI / DEGREES_PER_HALF_TURN;
  const horizonReach = 1 / Math.cos(tiltRadians) - 1;
  return WORLD_MAP_CLUSTER_BBOX_PADDING_RATIO + horizonReach;
}

/**
 * Grows the extent by a fraction of its own span so clusters just off-screen are
 * already formed by the time the user pans them into view.
 */
function getPaddedBbox(extent, paddingRatio) {
  const lngSpan = extent.xmax - extent.xmin;
  const latSpan = extent.ymax - extent.ymin;
  if (lngSpan >= WORLD_MAP_LONGITUDE_SPAN) {
    return WORLD_BBOX;
  }
  const lngPadding = lngSpan * paddingRatio;
  const latPadding = latSpan * paddingRatio;
  if (lngSpan + lngPadding * 2 >= WORLD_MAP_LONGITUDE_SPAN) {
    return [WORLD_MAP_MIN_LONGITUDE, clampLatitude(extent.ymin - latPadding), WORLD_MAP_MAX_LONGITUDE, clampLatitude(extent.ymax + latPadding)];
  }
  return [normalizeLongitude(extent.xmin - lngPadding), clampLatitude(extent.ymin - latPadding), normalizeLongitude(extent.xmax + lngPadding), clampLatitude(extent.ymax + latPadding)];
}

/**
 * Translates the camera into a supercluster query.
 *
 * On a globe `view.extent` stops describing the on-screen area once the horizon
 * is visible, so below {@link WORLD_MAP_CLUSTER_WORLD_BBOX_MAX_ZOOM} — or whenever
 * the extent is missing — the whole world is queried instead. The bias is
 * deliberate: extra off-screen clusters are invisible, missing ones are not.
 */
export function getClusterViewport(zoom, extent, tiltDegrees = 0) {
  const clusterZoom = Math.max(0, Math.round(zoom ?? 0));
  if (!extent || clusterZoom <= WORLD_MAP_CLUSTER_WORLD_BBOX_MAX_ZOOM) {
    return {
      bbox: WORLD_BBOX,
      zoom: clusterZoom
    };
  }
  return {
    bbox: getPaddedBbox(extent, getPaddingRatio(tiltDegrees)),
    zoom: clusterZoom
  };
}

/**
 * Stable key for a viewport, so a settle that did not actually move the camera
 * does not trigger a rebuild of every graphic (which would flicker).
 */
export function getClusterViewportKey({
  bbox,
  zoom
}) {
  const roundedBbox = bbox.map(value => value.toFixed(WORLD_MAP_CLUSTER_BBOX_KEY_PRECISION));
  return [zoom, ...roundedBbox].join("|");
}